import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useAuth } from "../../../../../context/AuthContext";
import styles from "./LoginForm.module.css";

/**
 * Reusable login form component with validation and error handling
 *
 * @param {Object} props - Component props
 * @returns {JSX.Element} Login form component
 */
const LoginForm = ({
  title = "Admin Login",
  redirectPath = "/login",
  onLoginSuccess,
  className,
}) => {
  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [validation, setValidation] = useState({
    email: { valid: true, message: "" },
    password: { valid: true, message: "" },
  });
  const [touched, setTouched] = useState({
    email: false,
    password: false,
  });

  // Get auth context
  const { login, loading: isLoading, error, isAuthenticated } = useAuth();

  // Extract error message
  const errorMessage = error;

  /**
   * Clear any auth errors
   */
  const clearError = useCallback(() => {
    // The AuthContext will handle error clearing automatically
  }, []);

  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} Whether email is valid
   */
  const validateEmail = useCallback((email) => {
    if (!email) {
      return { valid: false, message: "Email is required" };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { valid: false, message: "Please enter a valid email address" };
    }

    return { valid: true, message: "" };
  }, []);

  /**
   * Validate password
   * @param {string} password - Password to validate
   * @returns {boolean} Whether password is valid
   */
  const validatePassword = useCallback((password) => {
    if (!password) {
      return { valid: false, message: "Password is required" };
    }

    if (password.length < 6) {
      return {
        valid: false,
        message: "Password must be at least 6 characters",
      };
    }

    return { valid: true, message: "" };
  }, []);

  /**
   * Handle email input change
   * @param {Event} e - Change event
   */
  const handleEmailChange = useCallback(
    (e) => {
      const value = e.target.value;
      setEmail(value);

      // Validate on change if field has been touched
      if (touched.email) {
        setValidation((prev) => ({
          ...prev,
          email: validateEmail(value),
        }));
      }
    },
    [touched.email, validateEmail]
  );

  /**
   * Handle password input change
   * @param {Event} e - Change event
   */
  const handlePasswordChange = useCallback(
    (e) => {
      const value = e.target.value;
      setPassword(value);

      // Validate on change if field has been touched
      if (touched.password) {
        setValidation((prev) => ({
          ...prev,
          password: validatePassword(value),
        }));
      }
    },
    [touched.password, validatePassword]
  );

  /**
   * Handle input blur for validation
   * @param {string} field - Field name
   */
  const handleBlur = useCallback(
    (field) => {
      setTouched((prev) => ({
        ...prev,
        [field]: true,
      }));

      if (field === "email") {
        setValidation((prev) => ({
          ...prev,
          email: validateEmail(email),
        }));
      } else if (field === "password") {
        setValidation((prev) => ({
          ...prev,
          password: validatePassword(password),
        }));
      }
    },
    [email, password, validateEmail, validatePassword]
  );

  /**
   * Validate form before submission
   * @returns {boolean} Whether form is valid
   */
  const validateForm = useCallback(() => {
    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);

    setValidation({
      email: emailValidation,
      password: passwordValidation,
    });

    setTouched({
      email: true,
      password: true,
    });

    return emailValidation.valid && passwordValidation.valid;
  }, [email, password, validateEmail, validatePassword]);

  /**
   * Handle form submission
   * @param {Event} e - Submit event
   */
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // Validate form
      if (!validateForm()) {
        return;
      }

      // Attempt login
      const result = await login(email, password);

      // Call success callback if provided
      if (result.success && onLoginSuccess) {
        onLoginSuccess();
      }
    },
    [email, password, login, validateForm, onLoginSuccess]
  );

  // Check if user is already authenticated
  React.useEffect(() => {
    if (isAuthenticated && onLoginSuccess) {
      onLoginSuccess();
    }
  }, [isAuthenticated, onLoginSuccess]);

  return (
    <>
      <div
        style={{
          textAlign: "center",
          padding: "16px 18px",
          margin: "0 auto 18px",
          maxWidth: "420px",
          background: "#fff3cd",
          border: "1px solid #ffeeba",
          borderRadius: "6px",
          color: "#856404",
        }}
      >
        <div
          style={{
            fontSize: "22px",
            fontWeight: 700,
            letterSpacing: "0.5px",
          }}
        >
          FOR ADMIN USE ONLY
        </div>
        <div style={{ fontSize: "13px", marginTop: "4px" }}>
          This site is currently in testing for regular users.
        </div>
      </div>
      <form
        className={`${styles.loginForm} ${className || ""}`}
        onSubmit={handleSubmit}
      >
        <h2 className={styles.formTitle}>{title}</h2>

      <div className={styles.formGroup}>
        <label htmlFor="email" className={styles.formLabel}>
          Email Address
        </label>
        <input
          id="email"
          type="email"
          className={`${styles.formInput} ${
            !validation.email.valid && touched.email ? styles.error : ""
          }`}
          value={email}
          onChange={handleEmailChange}
          onBlur={() => handleBlur("email")}
          placeholder="Enter your email"
          disabled={isLoading}
          required
        />
        {!validation.email.valid && touched.email && (
          <span className={styles.inputError}>{validation.email.message}</span>
        )}
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="password" className={styles.formLabel}>
          Password
        </label>
        <input
          id="password"
          type="password"
          className={`${styles.formInput} ${
            !validation.password.valid && touched.password ? styles.error : ""
          }`}
          value={password}
          onChange={handlePasswordChange}
          onBlur={() => handleBlur("password")}
          placeholder="Enter your password"
          disabled={isLoading}
          required
        />
        {!validation.password.valid && touched.password && (
          <span className={styles.inputError}>
            {validation.password.message}
          </span>
        )}
      </div>

      {errorMessage && (
        <div className={styles.errorMessage}>{errorMessage}</div>
      )}

      <div className={styles.formActions}>
        <button
          type="submit"
          className={styles.submitButton}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span className={styles.loadingSpinner}></span>
              Signing in...
            </>
          ) : (
            "Sign in"
          )}
        </button>
      </div>
    </form>
    </>
  );
};

LoginForm.propTypes = {
  title: PropTypes.string,
  redirectPath: PropTypes.string,
  onLoginSuccess: PropTypes.func,
  className: PropTypes.string,
};

export default LoginForm;
